import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card, Overline } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Building2, Target, Bell, Plug, Puzzle } from "lucide-react";

const INTEGRATIONS = [
  "ScratchHQ Tee Sheet", "ScratchHQ POS", "ScratchHQ Payments", "Online Golfer Booking",
  "Golf-Cart GPS", "SMS / Email Marketing", "Dynamic Pricing", "Accounting Sync",
];

export default function Settings() {
  const { user, course } = useApp();
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings").then(({ data }) => setS(data)); }, []);

  const update = async (patch) => {
    setS((cur) => ({ ...cur, ...patch }));
    await api.put("/settings", patch);
    toast.success("Settings saved");
  };
  if (!s) return <div className="h-96 animate-pulse bg-white border border-[#E5E7EB] rounded-xl" />;

  return (
    <div className="space-y-8 max-w-4xl">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-[#1C1F1E]">Settings</h1>
        <p className="text-[#52525B] mt-1">Course configuration and platform preferences.</p>
      </div>

      <Card className="p-6">
        <div className="flex items-center gap-2 mb-5"><Building2 size={17} className="text-[#0F3F2D]" /><Overline>Course</Overline></div>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Course Name" value={course?.name} />
          <Field label="Location" value={course?.location} />
          <Field label="General Manager" value={user?.full_name} />
          <Field label="Email" value={user?.email} />
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center gap-2 mb-5"><Target size={17} className="text-[#0F3F2D]" /><Overline>Operational Targets</Overline></div>
        <div className="grid sm:grid-cols-3 gap-5">
          <div>
            <label className="text-sm text-[#52525B]">Target Round Time</label>
            <input data-testid="setting-target" value={s.target_round} onChange={(e) => setS({ ...s, target_round: e.target.value })} onBlur={(e) => update({ target_round: e.target.value })}
              className="w-full mt-1.5 px-3 py-2 rounded-lg border border-[#E5E7EB] font-mono2 text-sm outline-none focus:border-[#0F3F2D]/40" />
          </div>
          <div>
            <label className="text-sm text-[#52525B]">Daily Capacity</label>
            <input data-testid="setting-capacity" type="number" value={s.capacity} onChange={(e) => setS({ ...s, capacity: Number(e.target.value) })} onBlur={(e) => update({ capacity: Number(e.target.value) })}
              className="w-full mt-1.5 px-3 py-2 rounded-lg border border-[#E5E7EB] font-mono2 text-sm outline-none focus:border-[#0F3F2D]/40" />
          </div>
          <div>
            <label className="text-sm text-[#52525B]">Twilight Start</label>
            <input data-testid="setting-twilight" value={s.twilight_start} onChange={(e) => setS({ ...s, twilight_start: e.target.value })} onBlur={(e) => update({ twilight_start: e.target.value })}
              className="w-full mt-1.5 px-3 py-2 rounded-lg border border-[#E5E7EB] font-mono2 text-sm outline-none focus:border-[#0F3F2D]/40" />
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center gap-2 mb-5"><Bell size={17} className="text-[#0F3F2D]" /><Overline>Notifications</Overline></div>
        <div className="space-y-1">
          {[["notifications", "Platform notifications", "Receive daily operational summaries"],
            ["pace_alerts", "Pace alerts", "Notify when groups fall behind target pace"],
            ["revenue_alerts", "Revenue alerts", "Surface leakage and pricing opportunities"]].map(([key, title, desc]) => (
            <div key={key} className="flex items-center justify-between py-3 border-b border-[#F1F3F2] last:border-0">
              <div><div className="text-sm font-medium text-[#1C1F1E]">{title}</div><div className="text-xs text-[#52525B]">{desc}</div></div>
              <Switch data-testid={`toggle-${key}`} checked={s[key]} onCheckedChange={(v) => update({ [key]: v })} />
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center gap-2 mb-2"><Plug size={17} className="text-[#0F3F2D]" /><Overline>Integrations Roadmap</Overline></div>
        <p className="text-sm text-[#52525B] mb-4">Connect the rest of your operation. Coming soon to ScratchHQ.</p>
        <div className="grid sm:grid-cols-2 gap-2">
          {INTEGRATIONS.map((i) => (
            <div key={i} className="flex items-center justify-between px-4 py-3 rounded-lg border border-[#E5E7EB] bg-[#F7F9F8]">
              <span className="flex items-center gap-2.5 text-sm text-[#1C1F1E]"><Puzzle size={15} className="text-[#A1A1AA]" />{i}</span>
              <span className="text-xs font-medium text-[#A1A1AA] bg-white border border-[#E5E7EB] px-2 py-0.5 rounded-full">Soon</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

const Field = ({ label, value }) => (
  <div>
    <div className="text-sm text-[#52525B]">{label}</div>
    <div className="mt-1.5 px-3 py-2 rounded-lg border border-[#E5E7EB] bg-[#F7F9F8] text-sm text-[#1C1F1E]">{value}</div>
  </div>
);
